import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Copy } from 'lucide-react';
import { convidarFormSchema, type ConvidarFormValores, type ConviteCriado, type OpcoesConcessao } from '@triade/contracts';
import { useConvidar } from '@/api/usuarios';
import { formatarDataHora } from '@/lib/format';
import { MensagemCampo } from '@/components/item-cadastro/dados-item';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/lib/api';
import { EditorAcessos } from './editor-acessos';
import { ordenarAcessos } from './tipos';

const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';

export interface DialogoConviteProps {
  aberto: boolean;
  aoFechar: () => void;
  opcoes: OpcoesConcessao;
}

/** Resultado do convite: confirma o envio por e-mail ou entrega o link para copiar (so aparece agora). */
export function ResultadoConvite({ resultado, aoCopiar }: { resultado: ConviteCriado; aoCopiar?: (link: string) => void }) {
  const [copiado, setCopiado] = useState(false);
  // Sem APP_URL a API devolve so o caminho (/convite?token=...): completa com a origem de onde o administrador esta
  const link = resultado.link && resultado.link.startsWith('/') ? `${window.location.origin}${resultado.link}` : resultado.link;
  async function copiar() {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      /* sem permissao do navegador: o campo continua selecionavel */
    }
    aoCopiar?.(link);
    setCopiado(true);
  }
  return (
    <div className="flex flex-col gap-3" data-testid="resultado-convite">
      {resultado.emailEnviado && (
        <p role="status" className="rounded-md bg-success-bg px-3 py-2 text-small text-success-ink">
          Convite enviado para {resultado.convite.email}. O link vale até {formatarDataHora(resultado.convite.expiraEm)}.
        </p>
      )}
      {resultado.envioFalhou && (
        <p role="alert" className="rounded-md bg-warning-bg px-3 py-2 text-small text-warning-ink">
          Não foi possível enviar o e-mail. O convite foi criado: copie o link abaixo e envie por outro meio.
        </p>
      )}
      {!resultado.emailEnviado && !resultado.envioFalhou && (
        <p role="status" className="rounded-md bg-line-soft px-3 py-2 text-small text-ink-body">
          O envio de e-mail não está configurado. Copie o link e envie ao convidado (vale até {formatarDataHora(resultado.convite.expiraEm)}).
        </p>
      )}
      {link && (
        <div>
          <Label htmlFor="link-convite" className={ROTULO}>
            Link do convite (use uma vez)
          </Label>
          <div className="flex gap-2">
            <Input id="link-convite" readOnly value={link} className="font-mono text-caption" onFocus={(e) => e.currentTarget.select()} />
            <Button type="button" variant="outline" onClick={() => void copiar()} className="shrink-0 gap-1.5">
              <Copy aria-hidden="true" className="size-4" />
              {copiado ? 'Copiado' : 'Copiar'}
            </Button>
          </div>
          <p className="mt-1 text-caption text-ink-muted">Este link só aparece agora. Quem o abrir define a própria senha; depois de usado ele deixa de valer.</p>
        </div>
      )}
    </div>
  );
}

/** Dialogo "Convidar usuario": e-mail, nome (opcional) e a grade de acessos. Valida com o schema dos contratos; a API confere o teto. */
export function DialogoConvite({ aberto, aoFechar, opcoes }: DialogoConviteProps) {
  const convidar = useConvidar();
  const [resultado, setResultado] = useState<ConviteCriado | null>(null);
  const form = useForm<ConvidarFormValores>({
    resolver: zodResolver(convidarFormSchema),
    defaultValues: { email: '', nome: '', acessos: [] },
    mode: 'onSubmit',
  });
  const { errors } = form.formState;
  const acessos = form.watch('acessos');

  function fechar() {
    setResultado(null);
    form.reset({ email: '', nome: '', acessos: [] });
    convidar.reset();
    aoFechar();
  }

  const enviar = form.handleSubmit(async (v) => {
    try {
      const r = await convidar.mutateAsync({ email: v.email, ...(v.nome.trim() ? { nome: v.nome.trim() } : {}), acessos: v.acessos });
      setResultado(r);
    } catch (e) {
      form.setError('root', { message: e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'Não foi possível criar o convite. Tente de novo.' });
    }
  });

  return (
    <Dialog open={aberto} onOpenChange={(o) => !o && fechar()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Convidar usuário</DialogTitle>
          <DialogDescription>
            O convidado recebe um link de uso único para definir a própria senha. Você só concede papéis até o seu próprio nível, nas suas empresas.
          </DialogDescription>
        </DialogHeader>

        {resultado ? (
          <>
            <ResultadoConvite resultado={resultado} />
            <DialogFooter>
              <Button type="button" onClick={fechar}>
                Concluir
              </Button>
            </DialogFooter>
          </>
        ) : (
          <form onSubmit={enviar} noValidate aria-label="Convidar usuário" className="flex flex-col gap-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="conv-email" className={ROTULO}>
                  E-mail
                </Label>
                <Input id="conv-email" type="email" autoComplete="off" aria-invalid={errors.email ? true : undefined} aria-describedby={errors.email ? 'conv-email-erro' : undefined} {...form.register('email')} />
                <MensagemCampo id="conv-email-erro" mensagem={errors.email?.message} />
              </div>
              <div>
                <Label htmlFor="conv-nome" className={ROTULO}>
                  Nome (opcional)
                </Label>
                <Input id="conv-nome" autoComplete="off" maxLength={120} {...form.register('nome')} />
                <MensagemCampo id="conv-nome-erro" mensagem={errors.nome?.message} />
              </div>
            </div>

            <div>
              <p className={ROTULO}>Acessos</p>
              <EditorAcessos
                idPrefixo="conv"
                opcoes={opcoes}
                valor={acessos}
                onChange={(v) => form.setValue('acessos', ordenarAcessos(v), { shouldDirty: true, shouldValidate: form.formState.isSubmitted })}
              />
              <MensagemCampo id="conv-acessos-erro" mensagem={errors.acessos?.message ?? (errors.acessos as { root?: { message?: string } } | undefined)?.root?.message} />
            </div>

            {errors.root && (
              <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-small text-danger-ink">
                {errors.root.message}
              </p>
            )}

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={fechar}>
                Cancelar
              </Button>
              <Button type="submit" disabled={convidar.isPending}>
                {convidar.isPending ? 'Criando convite…' : 'Criar convite'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
